"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, Wrench, MapPin, Star, Code, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Calendar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const processSteps = [{
  number: "01",
  title: "1. SEO Audit & Tech Discovery",
  subtitle: "Uncovering Technical Blockers",
  description: "I personally perform an exhaustive technical crawl, site architecture check, and search intent audit to pinpoint exact issues capping your organic rankings.",
  deliverable: "Prioritized Technical Roadmap & Audit Log",
  icon: "Search"
}, {
  number: "02",
  title: "2. Strategic Optimization",
  subtitle: "Intent Alignment & On-Page",
  description: "I re-engineer high-intent target pages, metadata, semantic structure, and internal link trees so search engines accurately index your core offerings.",
  deliverable: "On-Page Optimization & Content Briefs",
  icon: "Wrench"
}, {
  number: "03",
  title: "3. Citations & Authority",
  subtitle: "Local & National Trust Building",
  description: "I audit and build consistent local citations, high-authority business profiles, and niche contextual backlinks that establish real search engine trust.",
  deliverable: "Verified Citation Ecosystem & Anchor Strategy",
  icon: "MapPin"
}, {
  number: "04",
  title: "4. Review & Reputation Strategy",
  subtitle: "Conversion & Local Signals",
  description: "I establish structured feedback workflows that turn happy customers into authentic Google reviews, boosting both local map pack rankings and user trust.",
  deliverable: "Automated Review Campaign Setup",
  icon: "Star"
}, {
  number: "05",
  title: "5. Structured Schema Data",
  subtitle: "AI Search & Entity Graph",
  description: "I write custom JSON-LD structured data to explicitly define your services, location, and organization entity for Google and AI search engines.",
  deliverable: "Custom JSON-LD Schema Implementation",
  icon: "Code"
}, {
  number: "06",
  title: "6. Reporting & ROI Tracking",
  subtitle: "Transparent Measurement",
  description: "I track organic traffic growth, keyword ranking improvements, and lead conversions through clear monthly reports, refining strategies based on hard data.",
  deliverable: "Monthly Consultation & Live KPI Dashboard",
  icon: "BarChart3"
}];
export default function MethodologyFlow() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".methodology-card", {
        y: 32
      });
      gsap.to(".methodology-card", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".methodology-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  const renderIcon = (name: string) => {
    const props = {
      className: "size-5 text-primary"
    };
    switch (name) {
      case "Search":
        return <Search {...props} />;
      case "Wrench":
        return <Wrench {...props} />;
      case "MapPin":
        return <MapPin {...props} />;
      case "Star":
        return <Star {...props} />;
      case "Code":
        return <Code {...props} />;
      case "BarChart3":
        return <BarChart3 {...props} />;
      default:
        return <Search {...props} />;
    }
  };
  return <section id="methodologyflow" data-nav="light" className="bg-background text-foreground relative py-20 lg:py-28 overflow-hidden">
      <div ref={container} className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
            My SEO Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground mb-6">
            A Structured 6-Step Methodology for Sustainable Organic Growth
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            I don&apos;t use automated templates or agency guesswork. Every stage of my SEO process is executed directly by me, targeted specifically to your revenue goals.
          </p>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {processSteps.map((step, i) => <div key={step.number} data-index={i} className="methodology-card flex" style={{
          opacity: 0
        }}>
              <Card className="bg-card text-card-foreground border border-border shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between w-full rounded-xl overflow-hidden group">
                <CardHeader className="pt-6 pb-4 px-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center justify-center size-10 rounded-lg bg-primary text-primary-foreground font-mono font-bold text-sm shadow-xs">
                      {step.number}
                    </span>
                    <div className="size-10 rounded-lg bg-secondary flex items-center justify-center border border-accent/20">
                      {renderIcon(step.icon)}
                    </div>
                  </div>
                  <Badge variant="outline" className="w-fit text-[13px] font-normal border-accent/40 text-muted-foreground mb-2">
                    {step.subtitle}
                  </Badge>
                  <CardTitle className="text-xl font-bold font-serif text-foreground group-hover:text-primary transition-colors">
                    {step.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="px-6 pb-6 pt-0 flex-1 flex flex-col justify-between">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed mb-6">
                    {step.description}
                  </CardDescription>

                  <div className="pt-4 border-t border-border mt-auto">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-xs font-medium text-foreground">
                        <strong className="text-muted-foreground font-normal">Deliverable: </strong>
                        {step.deliverable}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>

        <div className="bg-muted/60 border border-border rounded-2xl p-8 md:p-10 text-center max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="text-left max-w-xl">
            <h3 className="text-xl md:text-2xl font-bold font-serif text-foreground mb-2">
              Ready to execute a tailored search strategy?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Book a free consultation directly with me. No account managers, no middle-men — just clear, actionable guidance.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="gap-2 font-medium">
              <Link to="/ContactPage">
                <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                <span>Book Free Consultation</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="gap-2 font-medium">
              <Link to="/CaseStudiesPage">
                <span>View Case Studies</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}